
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, AlertTriangle } from 'lucide-react';
import { formatCurrency } from '@/utils/dateUtils';
import { Button } from '@/components/ui/button';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { fetchUpcomingPayments, UpcomingPayment } from '../services/upcomingPaymentsService';

export const UpcomingPaymentsPanel = () => {
  const [upcomingPayments, setUpcomingPayments] = useState<UpcomingPayment[]>([]);
  const [loading, setLoading] = useState(true);

  const loadUpcomingPayments = async () => {
    setLoading(true);
    try {
      // Уменьшаем период до 7 дней для панели предстоящих платежей
      const payments = await fetchUpcomingPayments(7);
      setUpcomingPayments(payments);
    } catch (err) {
      console.error('Ошибка при загрузке предстоящих платежей:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUpcomingPayments();
    
    // Обновляем каждый час
    const interval = setInterval(loadUpcomingPayments, 3600000);
    return () => clearInterval(interval);
  }, []);

  // Функция для форматирования текста о днях
  const getDaysText = (days: number): string => {
    if (days === 0) return 'сегодня';
    if (days === 1) return 'завтра';
    
    const word = days <= 4 ? (days === 1 ? 'день' : 'дня') : 'дней';
    return `через ${days} ${word}`;
  };

  const formatPaymentDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU');
  };

  if (loading) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="h-5 w-5" />
            Предстоящие платежи
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5" /> 
            Предстоящие платежи
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={loadUpcomingPayments}
          >
            Обновить
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {upcomingPayments.length === 0 ? (
          <p className="text-center text-muted-foreground py-4">
            Нет предстоящих платежей в ближайшие 7 дней
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Курьер</TableHead>
                <TableHead className="text-right">Платеж</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {upcomingPayments.map(payment => (
                <TableRow key={payment.subscription_id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{payment.courier_name}</p>
                      <p className="text-sm text-muted-foreground">#{payment.human_number}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div>
                      <p className="font-medium">{formatCurrency(payment.amount)}</p>
                      <p className={`text-sm flex items-center justify-end ${payment.days_until_payment <= 1 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                        {payment.days_until_payment <= 1 && <AlertTriangle className="h-3 w-3 mr-1" />}
                        {formatPaymentDate(payment.next_payment_date)} ({getDaysText(payment.days_until_payment)})
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
